import React, { useState, useEffect, useRef, useMemo } from 'react';
import { IoCaretBackSharp, IoCaretForwardSharp } from 'react-icons/io5';
import { GrGallery } from "react-icons/gr";
import { generateUrl } from '../Explore/services';

interface Photo {
  id?: string;
  url: string;
  label?: string;
}

interface GalleryProps {
  images: Photo[]; // Full gallery array
  openGallery: (index: number) => void; // Function to open modal
}

const GalleryGrid: React.FC<GalleryProps> = ({ images, openGallery }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Cache images in memory
  const cachedImages = useRef<Record<string, string>>({});

  useEffect(() => {
    images.forEach((img) => {
      if (!cachedImages.current[img.url]) {
        const image = new Image();
        image.src = img.url;
        cachedImages.current[img.url] = img.url;
      }
    });
  }, [images]);

  // Preview photos for desktop (first 5)
  const previewPhotos = useMemo(() => images.slice(0, 5), [images]);

  return (
    <div className="max-w-6xl mx-auto sm:px-0 lg:px-4 py-0 mt-17">
      {/* Desktop Grid */}
      <div className="hidden lg:grid lg:grid-cols-[2fr_1fr] gap-1 rounded-xl overflow-hidden max-h-[380px]">
        {/* Left - Main Cover Photo */}
        <div
          className="relative cursor-pointer group"
          onClick={() => openGallery(0)}
        >
          <img
            src={cachedImages.current[previewPhotos[0]?.url] || previewPhotos[0]?.url}
            alt={previewPhotos[0]?.label || ''}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-200" />
        </div>

        {/* Right - 4 Photos Grid */}
        <div className="grid grid-rows-2 grid-cols-2 gap-1 h-full">
          {previewPhotos.slice(1, 5).map((photo, index) => (
            <div
              key={photo.id || photo.url}
              className="relative cursor-pointer group"
              onClick={() => openGallery(index + 1)}
            >
              <img
                src={cachedImages.current[photo.url] || photo.url}
                alt={photo.label || ''}
                loading="lazy"
                className="w-full h-full object-cover"
              />

              <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-200" />

              {index === 3 && images.length > 5 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openGallery(0);
                    }}
                    className="bg-white text-gray-900 px-2 py-1 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center space-x-2"
                  >
                    <span className="text-lg">⋮⋮</span>
                    <span>Show all {images.length} photos</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile/Tablet */}
      <div className="lg:hidden relative" onClick={() => openGallery(0)}>
        <div className="relative overflow-hidden">
            <img
            src={cachedImages.current[images[currentIndex]?.url] || images[currentIndex]?.url}
            alt={images[currentIndex]?.label || ''}
            loading="lazy"
            className="w-full h-64 sm:h-80 object-cover"
            />

          {/* Prev Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex((prev) =>
                prev === 0 ? images.length - 1 : prev - 1
              );
            }}
            className="absolute top-1/2 left-2 transform -translate-y-1/2 hover:bg-opacity-100 transition-colors"
          >
            <IoCaretBackSharp style={{ opacity: 0.8}} size={24} className='text-white opacity-80 hover:opacity-100 transition-opacity'/>
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex((prev) =>
                prev === images.length - 1 ? 0 : prev + 1
              );
            }}
            className="absolute top-1/2 right-2 transform -translate-y-1/2"
          >
            <IoCaretForwardSharp style={{ opacity: 0.8}} size={24} className='text-white opacity-80 hover:opacity-100 transition-opacity'
            />
        </button>

          {/* Show All Button */}
          {images.length > 5 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openGallery(currentIndex);
              }}
              className="absolute bottom-4 right-4 text-white px-2 py-1 bg-gradient-to-br from-rose-500 to-orange-500 lg:px-4 lg:py-2 rounded-lg font-medium shadow-lg hover:bg-gray-100 transition-colors flex items-center lg:space-x-2"
            >
              <span className="flex text-sm items-center gap-1 "><GrGallery size={12} />{images.length}</span>
              <span className='hidden lg:block'>Show all</span>
            </button>
          )}
        </div>

        {/* Horizontal Scroll Thumbnails */}
        <div className="mt-1 flex space-x-2 overflow-x-auto px-2">
          {images.map((photo, idx) => (
            <img
              key={photo.id || photo.url}
              src={generateUrl(cachedImages.current[photo.url] || photo.url)}
              alt={photo.label || ''}
              loading="lazy"
              onClick={() => setCurrentIndex(idx)}
              className={`w-20 h-20 object-cover rounded-lg cursor-pointer border-2 ${
                idx === currentIndex ? 'border-blue-500' : 'border-transparent'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GalleryGrid;
